"use client"

import { useSyncExternalStore } from "react"
import type { ToastData, ToastStatus } from "./toast"
import type { ToastInput } from "./toast-stack"

/**
 * App-global toast store.
 *
 * `useToastStack` keeps its toasts in component state, which suits a self-
 * contained demo but cannot serve code that is nowhere near the renderer — a
 * mutation hook raising "Chat renamed", say. This module holds the queue outside
 * React so `toast()` is callable from anywhere, and `<ToastHost />` renders it
 * once at the app root.
 */

const DEFAULT_DURATION = 4200

let toasts: ToastData[] = []
const listeners = new Set<() => void>()
const timers = new Map<string, ReturnType<typeof setTimeout>>()
let idSeed = 0

function emit() {
  // A fresh array each time, so useSyncExternalStore sees a new reference.
  toasts = [...toasts]
  listeners.forEach((listener) => listener())
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

function getSnapshot() {
  return toasts
}

/** Server render has no toasts, and the snapshot must be referentially stable. */
const EMPTY: ToastData[] = []
function getServerSnapshot() {
  return EMPTY
}

export function dismissToast(id: string) {
  const timer = timers.get(id)
  if (timer !== undefined) {
    clearTimeout(timer)
    timers.delete(id)
  }
  toasts = toasts.filter((entry) => entry.id !== id)
  emit()
}

export function clearToasts() {
  timers.forEach((timer) => clearTimeout(timer))
  timers.clear()
  toasts = []
  emit()
}

function push(input: ToastInput): string {
  const id = input.id ?? `toast-${idSeed++}`
  const entry: ToastData = {
    duration: DEFAULT_DURATION,
    dismissible: true,
    ...input,
    id,
    createdAt: Date.now(),
  }

  // Re-pushing an existing id replaces it rather than stacking a duplicate,
  // which is what makes a loading toast upgradable to success in place.
  const isReplacement = toasts.some((candidate) => candidate.id === id)
  toasts = isReplacement
    ? toasts.map((candidate) => (candidate.id === id ? entry : candidate))
    : [...toasts, entry]

  const previousTimer = timers.get(id)
  if (previousTimer !== undefined) clearTimeout(previousTimer)

  // `loading` has no natural end, so it stays until replaced or dismissed.
  if (entry.duration && entry.duration > 0 && entry.status !== "loading") {
    timers.set(
      id,
      setTimeout(() => dismissToast(id), entry.duration),
    )
  }

  emit()
  return id
}

type ToastOptions = Omit<ToastInput, "title" | "status">

function withStatus(status: ToastStatus) {
  return (title: ToastInput["title"], options?: ToastOptions) =>
    push({ ...options, title, status })
}

/**
 * Raises a toast from anywhere. Requires `<ToastHost />` mounted once at the app
 * root; without it the queue fills and nothing renders.
 */
export const toast = Object.assign(
  (title: ToastInput["title"], options?: ToastOptions) => push({ ...options, title }),
  {
    success: withStatus("success"),
    error: withStatus("error"),
    info: withStatus("info"),
    loading: withStatus("loading"),
    neutral: withStatus("neutral"),
    dismiss: dismissToast,
    clear: clearToasts,
  },
)

/** Subscribes a component to the global queue. Used by `ToastHost`. */
export function useToasts(): ToastData[] {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
}
